import { MessagesSquare } from "lucide-react";
import { useMemo, useState } from "react";
import { useStore } from "zustand";
import { coreStore } from "@/lib/stores/core-store";
import { uiStore } from "@/lib/stores/ui-store";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";

const MS_PER_MINUTE = 60_000;
const MS_PER_HOUR = 3_600_000;
const MS_PER_DAY = 86_400_000;
const DAYS_IN_WEEK = 7;

export function ThreadsPopover({ channelId }: { channelId: string }) {
	const [isOpen, setIsOpen] = useState(false);
	const channels = useStore(coreStore, (state) => state.channels);
	const messages = useStore(coreStore, (state) => state.messages);
	const setChannelViewOption = useStore(
		uiStore,
		(state) => state.setChannelViewOption
	);

	const currentChannel = channels.get(channelId);

	const threads = useMemo(() => {
		const msgs = Array.from(messages.values());
		return Array.from(channels.values())
			.filter((channel) => channel.parentId === channelId && channel.isThread)
			.map((channel) => {
				const lastMessage = msgs.find((msg) => msg.channelId === channel.id);
				return {
					channelId: channel.id,
					lastMessage,
					name: channel.name,
				};
			})
			.sort((a, b) => {
				if (a.lastMessage?.createdAt && b.lastMessage?.createdAt) {
					return (
						new Date(b.lastMessage.createdAt).getTime() -
						new Date(a.lastMessage.createdAt).getTime()
					);
				}
				return 0;
			});
	}, [channels, messages, channelId]);

	if (currentChannel?.isThread) {
		return null;
	}

	const handleThreadClick = (threadChannelId: string) => {
		setChannelViewOption(channelId, (prev) => ({
			...prev,
			sidePanel: {
				type: "thread",
				threadId: threadChannelId,
			},
		}));
		setIsOpen(false);
	};

	return (
		<Popover onOpenChange={setIsOpen} open={isOpen}>
			<Tooltip>
				<TooltipTrigger asChild>
					<PopoverTrigger asChild>
						<Button size="icon" variant="ghost">
							<MessagesSquare />
						</Button>
					</PopoverTrigger>
				</TooltipTrigger>
				<TooltipContent>View threads</TooltipContent>
			</Tooltip>
			<PopoverContent
				align="end"
				className="max-h-[66vh] w-72 overflow-y-auto p-0"
			>
				<div className="flex flex-col">
					<div className="sticky top-0 border-b bg-popover px-4 py-3">
						<div className="flex items-center justify-between">
							<h3 className="font-semibold text-sm">
								Threads ({threads.length})
							</h3>
							<Button
								onClick={() => {
									setChannelViewOption(channelId, (prev) => ({
										...prev,
										sidePanel: {
											type: "create-thread",
										},
									}));
									setIsOpen(false);
								}}
								size="sm"
								variant="outline"
							>
								Create Thread
							</Button>
						</div>
					</div>
					{threads.length === 0 ? (
						<div className="flex items-center justify-center py-8 text-muted-foreground text-sm">
							No threads yet
						</div>
					) : (
						<div className="divide-y">
							{threads.map((thread) => (
								<button
									className="w-full px-4 py-3 text-left text-sm transition-colors hover:bg-accent"
									key={thread.channelId}
									onClick={() => handleThreadClick(thread.channelId)}
									type="button"
								>
									<div className="truncate font-medium text-foreground">
										{thread.name}
									</div>
									<div className="flex w-full justify-between">
										<div className="mt-1 line-clamp-2 text-muted-foreground text-xs">
											{thread.lastMessage?.richContent
												? extractTextFromRichContent(
														thread.lastMessage.richContent as Record<
															string,
															unknown
														>
													)
												: "No content"}
										</div>
										{thread.lastMessage?.createdAt && (
											<div className="mt-1 text-muted-foreground text-xs">
												{formatDate(new Date(thread.lastMessage.createdAt))}
											</div>
										)}
									</div>
								</button>
							))}
						</div>
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
}

type TiptapNode = {
	type: string;
	content?: Array<{ text?: string }>;
};

function extractTextFromRichContent(
	richContent: Record<string, unknown>
): string {
	if (!richContent || typeof richContent !== "object") return "";

	if ("content" in richContent && Array.isArray(richContent.content)) {
		const content = richContent.content as TiptapNode[];
		return content
			.map((node) => {
				if (node.type === "paragraph" && node.content) {
					return node.content.map((item) => item.text || "").join("");
				}
				return "";
			})
			.join(" ")
			.trim();
	}

	return "";
}

function formatDate(date: Date): string {
	const now = new Date();
	const diffMs = now.getTime() - date.getTime();
	const diffMins = Math.floor(diffMs / MS_PER_MINUTE);
	const diffHours = Math.floor(diffMs / MS_PER_HOUR);
	const diffDays = Math.floor(diffMs / MS_PER_DAY);

	if (diffMins < 1) return "just now";
	if (diffMins < 60) return `${diffMins}m ago`;
	if (diffHours < 24) return `${diffHours}h ago`;
	if (diffDays < DAYS_IN_WEEK) return `${diffDays}d ago`;

	return date.toLocaleDateString();
}
